import { AnyObject } from '@store/types';
import { EhrFieldsVersionParams } from 'chaika-frontend-types/frontend-cms';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

import { IField } from '@store/models/EntryFields';
import { List } from '@store/abstract/List';
import { SelectedTableFieldParams } from '@patient/ui/shared/store';

import { FieldHistory } from './FieldHistory';
import { uniqHistoryByValue } from './utils/uniqHistoryByValue';

export interface FiledValueParams<T> {
    entryId: string;
    creationTime: string;
    value: T;
    field: IField;
    author: string;
    authorId: string;
}

export class FieldHistoryList extends List<FieldHistory, EhrFieldsVersionParams | any> {
    protected identifiableFieldName = 'entryId';

    public isFieldListValue(name: string): boolean {
        const fieldValue = this.values.find(value => value.getField(name));

        return Boolean(fieldValue && fieldValue.getFieldValue(name) instanceof List);
    }

    public getFieldValues<T>(name: string): FiledValueParams<T>[] {
        return compact(
            this.values.map(value =>
                value.getField(name)
                    ? {
                          entryId: value.id,
                          creationTime: value.creationTime,
                          value: value.getFieldValue(name) as T,
                          field: value.getField(name),
                          author: value.author,
                          authorId: value.authorId,
                      }
                    : null,
            ),
        ).sort((a, b) => b.creationTime.localeCompare(a.creationTime));
    }

    public getFieldValuesByNameAndId<T>(name: string, id: string | number): FiledValueParams<T>[] {
        const fieldValues = this.getFieldValues(name);

        const result: FiledValueParams<T>[] = [];

        fieldValues.forEach(fieldValue => {
            if (fieldValue.value instanceof List) {
                const item = fieldValue.value.get(id);

                if (item) {
                    result.push(fieldValue as FiledValueParams<T>);
                }
            }
        });

        return result;
    }

    public getUniqNotEmptyTableFieldValues<T>(
        selectedTableField: SelectedTableFieldParams,
        authorId?: string,
    ): FiledValueParams<T>[] {
        const values = this.getFieldValues<T>(selectedTableField.fieldName);
        const filteredValues = values.filter(value => {
            return (
                value.field.hasData &&
                !isNil((value.value as AnyObject)[selectedTableField.cellName]) &&
                (value.value as AnyObject)[selectedTableField.cellName] !== ''
            );
        });

        return uniqHistoryByValue(
            filteredValues.map(value => ({
                ...value,
                value: (value.value as AnyObject)[selectedTableField.cellName],
            })),
            authorId,
        );
    }

    public getUniqNotEmptyFieldValues<T>(name: string, authorId?: string): FiledValueParams<T>[] {
        return uniqHistoryByValue(this.getNotEmptyFieldValues<T>(name), authorId);
    }

    public getNotEmptyFieldValues<T>(name: string): FiledValueParams<T>[] {
        const values = this.getFieldValues<T>(name);

        return values.filter(value => value.field.hasData);
    }

    public getNotEmptyFieldNames(): Set<string> {
        const fieldNames = new Set<string>();

        this.values.forEach(version => {
            const versionFieldNames = version.getFieldNames();
            versionFieldNames.forEach(name => fieldNames.add(name));
        });

        return fieldNames;
    }

    public getNotEmptyFieldValuesEntryIds(name: string): string[] {
        const values = this.getNotEmptyFieldValues(name);

        return values.map(value => value.entryId);
    }

    public getUniqFieldValuesFieldIds(name: string): string[] {
        const fieldValues = this.getFieldValues(name);

        const ids: string[] = [];

        fieldValues.forEach(({ value }) => {
            if (value instanceof List) {
                const fieldValueIds = value.ids as string[];
                ids.push(...fieldValueIds);
            }
        });

        return uniq(ids);
    }

    protected create(params: EhrFieldsVersionParams): FieldHistory {
        const entryHistory = new FieldHistory(params);

        return entryHistory;
    }
}
